// ============================================
// ZONE DE RÉPONSE — hauteur automatique
// ============================================
// Demande de Nathan (19/10/2026) : « le champ de réponse est trop petit ».
// Sur ordinateur, la zone fait au moins 8 lignes (180 px), grandit avec le
// texte jusqu'à la moitié de l'écran, puis défile. Sur téléphone, 5 lignes au
// moins. La poignée permet de l'agrandir à la main : la hauteur choisie
// devient le minimum. « Agrandir » la passe en grand (60 % de l'écran).
// Calcul pur ; le hook useHauteurAuto l'applique à la zone.

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** 8 lignes de 20 px (texte 14 px) + marges intérieures. */
export const HAUTEUR_MIN_ORDINATEUR = 180;
/** 5 lignes de 24 px (texte 16 px sur téléphone) + marges intérieures. */
export const HAUTEUR_MIN_TELEPHONE = 140;
/** Part de l'écran au-delà de laquelle la zone défile. */
export const PART_ECRAN_MAX = 0.5;
/** Part de l'écran occupée en mode « Agrandir ». */
export const PART_ECRAN_AGRANDIE = 0.6;

export interface BornesZone { min: number; max: number }

/**
 * Hauteurs minimale et maximale de la zone, selon l'écran.
 * `hauteurManuelle` (poignée) relève le minimum, et le maximum s'il le faut.
 */
export function bornesZoneSaisie(o: {
  hauteurEcran: number;
  telephone: boolean;
  agrandie?: boolean;
  hauteurManuelle?: number | null;
}): BornesZone {
  const ecran = Number.isFinite(o.hauteurEcran) && o.hauteurEcran > 0 ? o.hauteurEcran : 800;
  const minBase = o.telephone ? HAUTEUR_MIN_TELEPHONE : HAUTEUR_MIN_ORDINATEUR;
  if (o.agrandie) {
    const grand = Math.max(minBase, Math.round(ecran * PART_ECRAN_AGRANDIE));
    return { min: grand, max: grand };
  }
  const manuelle = o.hauteurManuelle && o.hauteurManuelle > 0 ? Math.round(o.hauteurManuelle) : 0;
  const min = Math.max(minBase, manuelle);
  const max = Math.max(min, Math.round(ecran * PART_ECRAN_MAX));
  return { min, max };
}

/**
 * Hauteur à appliquer pour un contenu de `contenu` px (scrollHeight, marges
 * intérieures comprises) et une bordure de `bordure` px : bornée entre min
 * et max ; au-delà du max, la zone défile.
 */
export function hauteurZoneSaisie(contenu: number, bornes: BornesZone, bordure = 0): { hauteur: number; defile: boolean } {
  const voulu = Math.ceil(Math.max(0, contenu) + Math.max(0, bordure));
  if (voulu > bornes.max) return { hauteur: bornes.max, defile: true };
  return { hauteur: Math.max(bornes.min, voulu), defile: false };
}

/**
 * Applique la hauteur automatique à une zone de saisie à chaque changement
 * de texte, d'écran ou de mode. Retient la hauteur choisie à la poignée.
 */
export function useHauteurAuto(
  ref: React.RefObject<HTMLTextAreaElement>,
  valeur: string,
  /** `pret` : passe à vrai quand la zone est montée (après un écran de chargement). */
  o: { telephone: boolean; agrandie: boolean; pret?: boolean },
): { noterRedimensionnement: () => void } {
  const [hauteurEcran, setHauteurEcran] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  const manuelle = useRef<number | null>(null);
  const appliquee = useRef<number | null>(null);
  const [version, setVersion] = useState(0);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const surRedim = () => setHauteurEcran(window.innerHeight);
    window.addEventListener('resize', surRedim);
    return () => window.removeEventListener('resize', surRedim);
  }, []);

  useLayoutEffect(() => {
    const zone = ref.current;
    if (!zone) return;
    const bornes = bornesZoneSaisie({ hauteurEcran, telephone: o.telephone, agrandie: o.agrandie, hauteurManuelle: manuelle.current });
    zone.style.height = 'auto';
    const bordure = zone.offsetHeight - zone.clientHeight;
    const { hauteur, defile } = hauteurZoneSaisie(zone.scrollHeight, bornes, bordure > 0 ? bordure : 0);
    zone.style.height = `${hauteur}px`;
    zone.style.overflowY = defile || o.agrandie ? 'auto' : 'hidden';
    appliquee.current = hauteur;
  }, [ref, valeur, hauteurEcran, o.telephone, o.agrandie, o.pret, version]);

  // Fin d'un glissement de la poignée : la hauteur choisie devient le minimum.
  const noterRedimensionnement = useCallback(() => {
    const zone = ref.current;
    if (!zone || o.agrandie) return;
    const h = zone.offsetHeight;
    if (appliquee.current !== null && Math.abs(h - appliquee.current) > 2) {
      manuelle.current = h;
      setVersion(v => v + 1);
    }
  }, [ref, o.agrandie]);

  return { noterRedimensionnement };
}
